import React, { ChangeEvent, ReactNode, useState } from 'react'

import { Field, FormInstance } from 'rc-field-form'
import { Meta, Rule } from 'rc-field-form/es/interface.d'
import styled, { css, CSS, useTheme } from 'styled-components'

import { Icon, IconEnum, IconSizeEnum } from '@atoms/index'
import { ResponsivePXValue, LiteBoxShadow } from '@components/Theme'

import { FormContext, FormContextProperties, FormValue, InputProps } from './Form'
import { InputWrapper } from './InputWrapper'
import { AsteriskContainer, AsteriskLabel, RequiredAsterisk } from './SelectInput'

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  .right-icon {
    position: absolute;
    ${ResponsivePXValue('right', '16px')}
  }
`

const Disabled = css`
  border-color: ${(props): string => props.theme.colors.whites.silver};
  color: ${(props): string => props.theme.colors.whites.silver};
  background-color: ${(props): string => props.theme.colors.whites.alabaster};
  ::placeholder {
    color: ${(props): string => props.theme.colors.whites.silver};
    opacity: 1;
  }
`

const MonthYearInputElement = styled.input<{ $error: boolean, disabled: boolean, $textAlign: string }>`
  font-family: 'gordita';
  font-weight: 400;
  border-radius: 4px;
  border-style: solid;
  border-width: 1px;

  text-align: ${(props): string => props.$textAlign};
  background-color: ${(props): string => props.$error ? props.theme.colors.whites.roseWhite : props.theme.colors.whites.pureWhite};
  border-color: ${(props): string => props.$error ? props.theme.colors.reds.cautionRed : props.theme.colors.whites.desertStorm};

  ${ResponsivePXValue('font-size', '14px')}
  ${ResponsivePXValue('padding', '0 12px')}
  ${ResponsivePXValue('width', 'CALC(100% - 26px)')}
  ${ResponsivePXValue('height', '40px')}

  &:focus {
    outline: none;
    box-shadow: none;
    border-color: ${(props): string => props.$error ? props.theme.colors.reds.cautionRed : props.theme.colors.slates.bitter};
    ${LiteBoxShadow}
  }

  &::placeholder {
    color: ${(props): string => props.theme.colors.whites.silver};
    opacity: 1;
  }

  ${(props): CSS => {
    if (props.disabled) {
      return Disabled
    }
  }}
`
export interface MonthYearInputProps extends InputProps {
  rules?: Rule[]
  placeholder?: string
  textAlign?: 'left' | 'right' | 'center'
  className?: string
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  absoluteError?: boolean
}

interface TextInputState {
  error: string
}

const DEFAULT_STATE = {
  error: '',
}
export function MonthYearInput(props: MonthYearInputProps): JSX.Element {
  const { placeholder = '', label, rules = [], showLabel, showOptional, name, textAlign = 'left', disabled, loading, className, onKeyDown, absoluteError } = props
  const theme = useTheme()
  const [state, setState] = useState<TextInputState>({ ...DEFAULT_STATE })
  return (
    <Field name={name} rules={rules}>
      {(control: { value?: FormValue, onChange?: (value: FormValue) => void, onBlur?: (value: FormValue) => void }, meta: Meta, form: FormInstance) => {
        const required = rules.some(rule => typeof rule === 'object' && rule.required)

        const { value, onChange, onBlur } = control

        const format = (val: string): string => {
          const digits = val.replace(/\D/g, '').slice(0, 4)
          if (digits.length <= 2) return digits
          return `${digits.slice(0, 2)}/${digits.slice(2)}`
        }

        const _handleChange = (e: ChangeEvent<HTMLInputElement>) => {
          const raw = e.target.value.replace(/\D/g, '').slice(0, 4)
          const formatted = format(raw)
          if (raw.length >= 2) {
            const mm = parseInt(raw.slice(0, 2), 10)
            if (mm < 1 || mm > 12) {
              return
            }
          }

          onChange?.(formatted)

        }

        const _handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
          const raw = e.target.value.replace(/\D/g, '').slice(0, 4)
          const formatted = format(raw)
          if (raw.length >= 2) {
            const mm = parseInt(raw.slice(0, 2), 10)
            if (mm < 1 || mm > 12) {
              return
            }
          }
          const error = meta.errors?.[0]
          setState((prevState) => ({
            ...prevState,
            error,
          }))
          onBlur?.(formatted)
        }

        const getInputLabel = (label: string): ReactNode | string => {
          if (required && !!label) {
            return (
              <AsteriskContainer>
                <AsteriskLabel>{label}</AsteriskLabel>
                <RequiredAsterisk>*</RequiredAsterisk>
              </AsteriskContainer>
            )
          }
          return label
        }

        const inputLabel = getInputLabel(label)

        return (
          <InputWrapper
            required={required}
            label={inputLabel}
            showLabel={showLabel}
            showOptional={showOptional}
            error={state.error}
            absoluteError={absoluteError}
            className={className}>
            <FormContext.Consumer>
              {({ loading: formLoading, disabled: formDisabled }: FormContextProperties) => (
                <Container>
                  <MonthYearInputElement
                    autoComplete='cc-exp'
                    disabled={loading || disabled || formLoading || formDisabled}
                    name={name}
                    $error={!!state.error}
                    value={value ? value + '' : ''}
                    placeholder={placeholder}
                    $textAlign={textAlign}
                    onChange={_handleChange}
                    onKeyDown={onKeyDown}
                    onBlur={_handleBlur}
                  />
                  {!!state.error && (
                    <Icon
                      containerClassName='right-icon'
                      size={IconSizeEnum.SMALL}
                      icon={IconEnum.WARNING_OUTLINE}
                      color={theme.colors.reds.cautionRed}
                    />
                  )}

                </Container>
              )}
            </FormContext.Consumer>
          </InputWrapper>
        )
      }}
    </Field>
  )
}
